import axios from 'axios';
import { getBaseURL } from '../services/apiClient';
import { getToken } from './jwtUtils';

/**
 * Normalizes a stored image path into a fully-qualified URL the browser can load.
 *
 * Stored paths come in a few shapes:
 *  - a full http(s) URL (S3 or external) — returned as-is
 *  - a bare/relative path (e.g. "/uploads/123.png" or "123.png") — resolved to
 *    the configured S3 bucket URL using the filename.
 */
export const normalizeImagePath = (path: string | null | undefined): string => {
  if (!path) return '';

  // Already a full URL (S3 or external).
  if (path.startsWith('http')) {
    return path;
  }

  const filename = path.split('/').pop();
  if (!filename) {
    return '';
  }

  const bucketName = process.env.REACT_APP_S3_BUCKET_NAME || 'mathwiz-images';
  const region = process.env.REACT_APP_AWS_REGION || 'us-east-1';

  return `https://${bucketName}.s3.${region}.amazonaws.com/uploads/${filename}`;
};

/**
 * Uploads a single image file to the backend upload endpoint and returns the
 * stored file path. Throws on failure so callers can surface the error.
 */
export const uploadImage = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('image', file);

  const response = await axios.post(
    `${getBaseURL()}/api/upload/file`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${getToken()}`
      }
    }
  );

  return response.data.filePath as string;
};
